import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  IconPencil, IconClipboardList, IconFlask, IconDatabase, IconDownload,
  IconPlayerStopFilled, IconCircleFilled,
} from "@tabler/icons-react";
import { cancelJob, listJobs } from "../services/api";

// Five surfaces (SPEC.md §6; Live Session removed 2026-10-20), grouped as in frontend-mockup.html. Icons are Tabler (consistent set).
export const NAV = [
  { group: "Author", items: [
    { to: "/prompt-studio", Icon: IconPencil, label: "Prompt Studio" },
    { to: "/cases", Icon: IconClipboardList, label: "Case Library" },
  ]},
  { group: "Execute", items: [
    { to: "/experiment", Icon: IconFlask, label: "Experiment" },
  ]},
  { group: "Data", items: [
    { to: "/runs", Icon: IconDatabase, label: "Results" },
    { to: "/export", Icon: IconDownload, label: "Export" },
  ]},
];

const TITLES = {
  "/prompt-studio": "Prompt Studio",
  "/cases": "Case Library",
  "/experiment": "Experiment",
  "/runs": "Results",
  "/export": "Export",
};

export default function Layout({ email, onSignOut }) {
  const { pathname } = useLocation();
  const title = TITLES[pathname] || (pathname.startsWith("/runs/") ? "Run detail" : "Mari Repeatability");
  const initials = (email || "dev").replace(/@.*/, "").slice(0, 2).toUpperCase();

  // Minimal cross-tab "active runs" indicator: poll the in-memory job registry.
  // Keep the running jobs themselves (not just a count) so the Stop button can cancel them.
  const [running, setRunning] = useState([]);
  const [stopping, setStopping] = useState(false);
  const refreshJobs = useCallback(
    () =>
      listJobs()
        // "preparing" = a Mari experiment pinning its florence-prompts commit before its runs start
        .then((d) => (d.jobs || []).filter((jb) => jb.status === "running" || jb.status === "preparing"))
        .catch(() => null),
    []
  );
  useEffect(() => {
    let alive = true;
    const tick = () => refreshJobs().then((jobs) => { if (alive && jobs) setRunning(jobs); });
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [refreshJobs]);
  const activeRuns = running.length;

  // Stop button: cancel every running job's background task. Confirm first (irreversible — a mid-run
  // cell is dropped; already-completed cells stay persisted).
  const stopRuns = async () => {
    const n = running.length;
    if (!n) return;
    if (!window.confirm(`Stop ${n} running ${n === 1 ? "job" : "jobs"}? In-progress runs will be cancelled.`)) return;
    setStopping(true);
    try {
      await Promise.allSettled(running.map((jb) => cancelJob(jb.job_id)));
      const jobs = await refreshJobs();
      if (jobs) setRunning(jobs);
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <div className="dot">M</div>
          <div>
            <b>Mari Repeatability</b>
            <span>Dev Console · Study 1</span>
          </div>
        </div>
        <nav className="nav">
          {NAV.map((section) => (
            <React.Fragment key={section.group}>
              <div className="nav-lbl">{section.group}</div>
              {section.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  className={({ isActive }) => (isActive ? "active" : undefined)}
                >
                  <span className="i"><it.Icon size={16} stroke={1.8} /></span> {it.label}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </nav>
        <div className="side-foot">
          <div className="avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{email || "dev (no auth)"}</div>
            {onSignOut ? (
              <button
                type="button"
                onClick={onSignOut}
                className="linklike"
                style={{ color: "var(--faint)", fontSize: 11, background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                Sign out
              </button>
            ) : (
              <span style={{ color: "var(--faint)", fontSize: 11 }}>local · no auth</span>
            )}
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <h1>{title}</h1>
          <div className="spacer" />
          {activeRuns > 0 && (
            <>
              <Link
                to="/runs"
                className="chip warn"
                style={{ textDecoration: "none", marginRight: 8 }}
                title="Active runs in progress — click to view Results"
              >
                <IconCircleFilled size={9} /> {activeRuns} running
              </Link>
              <button
                type="button"
                onClick={stopRuns}
                disabled={stopping}
                className="chip bad"
                style={{
                  marginRight: 8,
                  border: "1px solid var(--bad)",
                  cursor: stopping ? "default" : "pointer",
                  opacity: stopping ? 0.6 : 1,
                }}
                title="Stop all running jobs — cancels any in-progress run"
              >
                {stopping ? "Stopping…" : <><IconPlayerStopFilled size={12} /> Stop</>}
              </button>
            </>
          )}
          <span className="pill dev">
            <span className="led" /> DEV
          </span>
        </div>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
